export type PipKind = "previz" | "animatic"

export type PipClip = {
  url: string
  sourceInMs: number
  sourceOutMs: number | null
}

export type PipSources = {
  previz: PipClip | null
  animatic: PipClip | null
}

export const EMPTY_PIP: PipSources = { previz: null, animatic: null }

/** Локальное время шота и ролики, которые в этот момент можно показать. */
export type PipFrame = {
  localMs: number
  sources: PipSources
}

export type StudioPipPayload = {
  previz?: RawPipClip | null
  animatic?: RawPipClip | null
} | null

type RawPipClip = {
  stream_url?: string | null
  source_in_ms?: number | null
  source_out_ms?: number | null
}

export type ShotPipPreviz = {
  url?: string | null
  duration_ms?: number | null
}

export type ShotPipAnimatic = {
  start_ms: number
  end_ms: number
  animatic_video_stream_url?: string | null
  reference_clip_with_audio_stream_url?: string | null
  reference_clip_stream_url?: string | null
}

const FRAME_SEC = 1 / 30

/**
 * Локальные мс шота → мс источника.
 * «freeze», когда окно ролика уже кончилось: хвост длиннее шота сюда не попадает,
 * потому что вызывающий ограничивает localMs длиной шота.
 */
export function pipSourceMs(localMs: number, clip: PipClip): number | "freeze" {
  const local = Math.max(0, localMs)
  if (clip.sourceOutMs != null) {
    const span = clip.sourceOutMs - clip.sourceInMs
    if (local >= span) return "freeze"
  }
  return clip.sourceInMs + local
}

/** Секунда в файле и флаг «держать последний кадр». */
export function pipTargetSec(
  clip: PipClip,
  localMs: number,
  mediaDurationSec: number,
): { sec: number; freeze: boolean } {
  const mapped = pipSourceMs(localMs, clip)
  const duration = Number.isFinite(mediaDurationSec) && mediaDurationSec > 0 ? mediaDurationSec : 0
  if (mapped === "freeze") {
    const end = clip.sourceOutMs != null ? clip.sourceOutMs / 1000 : duration
    const capped = duration > 0 ? Math.min(end, duration) : end
    return { sec: Math.max(0, capped - FRAME_SEC), freeze: true }
  }
  const sec = mapped / 1000
  if (duration > 0 && sec >= duration - FRAME_SEC) {
    return { sec: Math.max(0, duration - FRAME_SEC), freeze: true }
  }
  return { sec, freeze: false }
}

export function pipKinds(sources: PipSources | null | undefined): PipKind[] {
  const kinds: PipKind[] = []
  if (sources?.previz) kinds.push("previz")
  if (sources?.animatic) kinds.push("animatic")
  return kinds
}

export function samePipKinds(a: readonly PipKind[], b: readonly PipKind[]): boolean {
  return a.length === b.length && a.every((kind, index) => kind === b[index])
}

function clipFromRaw(raw: RawPipClip | null | undefined): PipClip | null {
  const url = raw?.stream_url
  if (!url) return null
  const sourceInMs = Math.max(0, Math.round(raw.source_in_ms ?? 0))
  const out = raw.source_out_ms
  const sourceOutMs = out != null && out > sourceInMs ? Math.round(out) : null
  return { url, sourceInMs, sourceOutMs }
}

export function pipSourcesFromPlayback(pip: StudioPipPayload | null | undefined): PipSources {
  if (!pip) return EMPTY_PIP
  return {
    previz: clipFromRaw(pip.previz),
    animatic: clipFromRaw(pip.animatic),
  }
}

export function pipSourcesFromShot(
  previz: ShotPipPreviz | null | undefined,
  seg: ShotPipAnimatic | null | undefined,
): PipSources {
  let previzClip: PipClip | null = null
  if (previz?.url) {
    const duration = previz.duration_ms
    previzClip = {
      url: previz.url,
      sourceInMs: 0,
      sourceOutMs: duration != null && duration > 0 ? Math.round(duration) : null,
    }
  }
  return { previz: previzClip, animatic: animaticClipFromShot(seg) }
}

function animaticClipFromShot(seg: ShotPipAnimatic | null | undefined): PipClip | null {
  if (!seg) return null
  const start = Math.round(seg.start_ms)
  const end = Math.round(seg.end_ms)
  const windowMs = end - start
  if (seg.animatic_video_stream_url && windowMs > 0) {
    return { url: seg.animatic_video_stream_url, sourceInMs: start, sourceOutMs: end }
  }
  const ref = seg.reference_clip_with_audio_stream_url || seg.reference_clip_stream_url
  if (ref && windowMs > 0) return { url: ref, sourceInMs: 0, sourceOutMs: windowMs }
  return null
}
